import { useCallback, useEffect, useRef, useState } from "react";
import { Landmark, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { type MpBrickController, unmountBrickSafely } from "@/lib/mp-brick";
import { loadMercadoPagoSdk } from "@/lib/mercadopago-sdk";
import { useBillingIdentity } from "@/hooks/useBillingIdentity";
import { useDirectPayment } from "@/hooks/useDirectPayment";
import { parsePsePayer, PSE_PAYMENT_METHOD_ID, pseInputFrom, type PseBrickFormData, type PseContact } from "@/lib/pse-payment";

const MP_PUBLIC_KEY = import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY || "";
const CONTAINER_ID = "mp-pse-payment-container";

const EMPTY_CONTACT: PseContact = {
  first_name: "",
  last_name: "",
  phone: "",
  street_name: "",
  street_number: "",
  neighborhood: "",
  city: "",
  federal_unit: "",
  zip_code: "",
};

const CONTACT_FIELDS: { key: keyof PseContact; label: string; placeholder?: string; inputMode?: "numeric" | "tel"; wide?: boolean }[] = [
  { key: "first_name", label: "Nombre del titular" },
  { key: "last_name", label: "Apellido del titular" },
  { key: "phone", label: "Teléfono", placeholder: "3001234567", inputMode: "tel" },
  { key: "zip_code", label: "Código postal", placeholder: "110111", inputMode: "numeric" },
  { key: "street_name", label: "Dirección", placeholder: "Calle 100", wide: true },
  { key: "street_number", label: "Número", placeholder: "15-20" },
  { key: "neighborhood", label: "Barrio" },
  { key: "city", label: "Ciudad" },
  { key: "federal_unit", label: "Departamento" },
];

export function PsePaymentForm({
  orgId,
  amount,
  description,
  payerEmail,
  mode,
  paymentIntentId,
  onError,
}: {
  orgId: string;
  amount: number;
  description?: string;
  payerEmail?: string;
  mode: string;
  paymentIntentId: string;
  onError?: (error: string) => void;
}) {
  const { identity } = useBillingIdentity(orgId);
  const [contact, setContact] = useState<PseContact>(EMPTY_CONTACT);
  const contactRef = useRef(contact);
  contactRef.current = contact;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const controllerRef = useRef<MpBrickController | null>(null);
  const submittedRef = useRef(false);
  const intentRef = useRef(paymentIntentId);
  const [ready, setReady] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const { pay, loading: paying } = useDirectPayment();

  useEffect(() => {
    if (!identity) return;
    setContact((current) => {
      const next = {
        ...current,
        first_name: current.first_name || identity.billing_first_name || "",
        last_name: current.last_name || identity.billing_last_name || "",
        phone: current.phone || identity.billing_phone || "",
      };
      const changed = next.first_name !== current.first_name || next.last_name !== current.last_name || next.phone !== current.phone;
      return changed ? next : current;
    });
  }, [identity]);

  const submit = useCallback(async (formData: PseBrickFormData) => {
    if (submittedRef.current) return;
    const input = pseInputFrom(contactRef.current, formData);
    const parsed = parsePsePayer(input);
    if ("error" in parsed) {
      setFormError(parsed.error);
      throw new Error(parsed.error);
    }
    setFormError(null);
    submittedRef.current = true;
    const res = await pay({
      organization_id: orgId,
      payment_method_id: PSE_PAYMENT_METHOD_ID,
      amount,
      payer_email: payerEmail,
      description,
      mode,
      payment_intent_id: intentRef.current,
      pse: input,
    });
    if (res.success && res.redirect_url) {
      setRedirecting(true);
      window.location.assign(res.redirect_url);
      return;
    }
    submittedRef.current = false;
    intentRef.current = crypto.randomUUID();
    const message = res.error || "No se pudo iniciar el pago por PSE";
    setFormError(message);
    onErrorRef.current?.(message);
    throw new Error(message);
  }, [amount, description, mode, orgId, pay, payerEmail]);

  const submitRef = useRef(submit);
  submitRef.current = submit;

  useEffect(() => {
    if (!MP_PUBLIC_KEY) return;
    let cancelled = false;
    setReady(false);
    setSdkError(null);
    (async () => {
      try {
        await loadMercadoPagoSdk();
        if (cancelled) return;
        const mp = new window.MercadoPago(MP_PUBLIC_KEY, { locale: "es-CO" });
        const controller = await mp.bricks().create("payment", CONTAINER_ID, {
          initialization: { amount, payer: payerEmail ? { email: payerEmail } : undefined },
          customization: {
            visual: { hideFormTitle: true, style: { theme: "flat" } },
            paymentMethods: { bankTransfer: ["pse"] },
          },
          callbacks: {
            onReady: () => setReady(true),
            onSubmit: ({ formData }: { formData: PseBrickFormData }) => submitRef.current(formData),
            onError: (error: unknown) => console.error("[PsePaymentForm] Brick error:", error),
          },
        });
        if (cancelled) {
          unmountBrickSafely(controller);
          return;
        }
        controllerRef.current = controller;
      } catch (err: unknown) {
        console.error("[PsePaymentForm] SDK/Brick init error:", err);
        if (!cancelled) setSdkError("No se pudo cargar el formulario de PSE");
      }
    })();
    return () => {
      cancelled = true;
      const controller = controllerRef.current;
      controllerRef.current = null;
      unmountBrickSafely(controller);
    };
  }, [amount, payerEmail]);

  if (redirecting) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <Landmark className="h-10 w-10 text-primary" />
        <p className="font-medium">Te estamos llevando a tu banco…</p>
        <p className="text-xs text-muted-foreground">
          Completa el pago en el portal de tu banco. Al terminar volverás a Flow y verás el estado del pago.
        </p>
      </div>
    );
  }

  if (sdkError) {
    return (
      <Alert className="border-destructive/30 bg-destructive/5">
        <AlertDescription>{sdkError}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        PSE debita de tu cuenta bancaria. El pago queda en proceso hasta que tu banco lo confirme; solo entonces se acredita.
      </p>
      <div className="grid grid-cols-2 gap-3">
        {CONTACT_FIELDS.map((field) => (
          <div key={field.key} className={`space-y-1 ${field.wide ? "col-span-2" : ""}`}>
            <Label htmlFor={`pse-${field.key}`}>{field.label}</Label>
            <Input
              id={`pse-${field.key}`}
              value={contact[field.key]}
              placeholder={field.placeholder}
              inputMode={field.inputMode}
              onChange={(e) => setContact((current) => ({ ...current, [field.key]: e.target.value }))}
            />
          </div>
        ))}
      </div>
      {formError && (
        <Alert className="border-destructive/30 bg-destructive/5">
          <AlertDescription>{formError}</AlertDescription>
        </Alert>
      )}
      {(!ready || paying) && (
        <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          {paying ? "Conectando con tu banco..." : "Cargando bancos..."}
        </div>
      )}
      <div id={CONTAINER_ID} className={!ready ? "hidden" : ""} />
    </div>
  );
}
